import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { Footer } from "../components/Footer";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function PrivacyPage() {
  useEffect(() => {
    document.title = "Privacy Policy - Afro Fanthem";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/"
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="back-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8" data-testid="privacy-headline">
            Privacy Policy
          </h1>

          <div className="prose prose-invert prose-gray max-w-none">
            <p className="text-gray-300 text-lg mb-8">
              Last updated: January 2026
            </p>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">1. Introduction</h2>
              <p className="text-gray-400 leading-relaxed">
                Afro Fanthem ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, and safeguard your personal information when you participate 
                in our campaign to unite African football fans and advocate for greater recognition from 
                football clubs and institutions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">2. Information We Collect</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                When you register and complete our survey, we collect the following information:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 mb-4">
                <li><strong className="text-white">Registration Information:</strong> Email address (required), country (required), city (optional), phone number (optional)</li>
                <li><strong className="text-white">Survey Responses:</strong> Your answers about club support, engagement preferences, and opinions</li>
                <li><strong className="text-white">Prize Draw Entry:</strong> If you opt in, your email and phone number for prize verification</li>
                <li><strong className="text-white">Technical Data:</strong> IP address (hashed for privacy), browser information, and timestamps</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Your information is used for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2">
                <li><strong className="text-white">Campaign Advocacy:</strong> Aggregated, anonymized data to demonstrate African fan engagement to football clubs and institutions</li>
                <li><strong className="text-white">Prize Draw:</strong> To contact winners and verify eligibility (phone number required for prize entry)</li>
                <li><strong className="text-white">Communication:</strong> To send important updates about the campaign (only if you've consented)</li>
                <li><strong className="text-white">Analytics:</strong> To understand our community and improve our advocacy efforts</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">4. Phone Number Usage</h2>
              <p className="text-gray-400 leading-relaxed">
                Phone numbers are collected for prize draw eligibility. We use phone numbers to:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 mt-4">
                <li>Verify your location through country code (helps reduce fraudulent entries)</li>
                <li>Contact prize draw winners</li>
                <li>Prevent duplicate or bot submissions</li>
              </ul>
              <p className="text-gray-400 leading-relaxed mt-4">
                Your phone number will never be sold, shared with third parties for marketing, or used 
                for any purpose other than prize draw verification.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">5. Data Sharing</h2>
              <p className="text-gray-400 leading-relaxed">
                We do not sell your personal information. Aggregated, anonymized statistics may be shared 
                with football clubs and institutions as part of our advocacy efforts. Individual responses 
                from the open-text question may be shared anonymously (without any identifying information) 
                to demonstrate fan sentiment.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">6. Data Security</h2>
              <p className="text-gray-400 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. This includes:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 mt-4">
                <li>Secure, encrypted database storage</li>
                <li>IP address hashing for privacy</li>
                <li>Protected admin access to data</li>
                <li>Regular security reviews</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">7. Data Retention</h2>
              <p className="text-gray-400 leading-relaxed">
                We retain your data for as long as necessary to fulfill the purposes outlined in this policy. 
                Survey data will be retained for the duration of the campaign and any follow-up advocacy work. 
                Prize draw entries will be retained until winners are selected and prizes distributed.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">8. Your Rights</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data (subject to legal obligations)</li>
                <li>Withdraw consent for communications at any time</li>
              </ul>
              <p className="text-gray-400 leading-relaxed mt-4">
                To exercise these rights, please contact us through our social media channels or via email.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">9. Cookies</h2>
              <p className="text-gray-400 leading-relaxed">
                We use essential cookies to maintain your session while completing the survey. We do not 
                use tracking cookies for advertising purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">10. Prize Draw Terms</h2>
              <p className="text-gray-400 leading-relaxed">
                The prize draw ends August 31, 2026. 100 winners will be randomly selected from eligible 
                entries (those who opted in and provided a valid phone number). Winners will receive 
                merchandise related to the club they selected in the survey. Winners will be contacted 
                via the phone number and/or email provided.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-400 leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page 
                with an updated revision date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-2xl text-white uppercase mb-4">12. Contact Us</h2>
              <p className="text-gray-400 leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please reach 
                out to us through:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 mt-4">
                <li>General inquiries: <a href="mailto:contact@afrofanthem.com" className="text-[#FBBF24] hover:underline">contact@afrofanthem.com</a></li>
                <li>Survey support: <a href="mailto:survey@afrofanthem.com" className="text-[#FBBF24] hover:underline">survey@afrofanthem.com</a></li>
                <li>Instagram: <a href="https://www.instagram.com/afrofanthem/" className="text-[#FBBF24] hover:underline">@afrofanthem</a></li>
                <li>X (Twitter): <a href="https://x.com/Afrofanthem" className="text-[#FBBF24] hover:underline">@Afrofanthem</a></li>
                <li>Email: afrofanthem@afrofanthem.com</li>
              </ul>
            </section>
          </div>

          {/* Back to Home */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-[#FBBF24] font-bold uppercase tracking-widest hover:gap-4 transition-all"
              data-testid="privacy-back-link"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Homepage
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
